import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Flex,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useVideoContext } from '../../../../hooks/use-video';
import { useStore } from '../../../../helpers/use-store';
import { LoadingState, useLoading } from '@monorepo/react-components';
import dayjs from 'dayjs';
import { useNotification } from '../../../../hooks/use-notification';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_TIMEZONE,
  PublishedAccount,
  SocialProvider,
  Timezone,
  TimezoneLabels,
  VideoStatus,
} from '@monorepo/types';

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { observer } from 'mobx-react-lite';
import ShareModal, { ShareModalActions } from './manual-share';
import SocialAccountTabs from './social-account-tabs';

dayjs.extend(utc);
dayjs.extend(timezone);

const SocialPublish = () => {
  const { t } = useTranslation();
  const shareModalRef = useRef<ShareModalActions>(null);
  const { video, fetchVideoAndAssets } = useVideoContext();
  const {
    dataStore: { videoStore, socialAccountStore },
  } = useStore();
  const { loadingState, updateLoadingState } = useLoading();
  const {
    loadingState: socialAccountLoadingState,
    updateLoadingState: updateSocialAccountLoadingState,
  } = useLoading();
  const notificationApi = useNotification();
  const [publishAt, setPublishAt] = useState<Date | undefined>(
    video?.socialPublishInfo?.publishAt
  );
  const [timezone, setTimezone] = useState<Timezone>(
    video?.socialPublishInfo?.timezone || DEFAULT_TIMEZONE
  );
  const [publishedAccounts, setPublishedAccounts] = useState<
    PublishedAccount[]
  >(video?.publishedAccounts || []);

  const getPlatformsMap = () => {
    const platforms = new Map<SocialProvider, PublishedAccount>();

    // Add platforms from published accounts
    publishedAccounts.forEach((account) => {
      platforms.set(account.socialPlatform, account);
    });

    return platforms;
  };

  const [platformsMap, setPlatformsMap] = useState<
    Map<SocialProvider, PublishedAccount>
  >(getPlatformsMap());
  const [activeTab, setActiveTab] = useState<SocialProvider | 'add'>(
    platformsMap.size > 0 ? Array.from(platformsMap.keys())[0] : 'add'
  );

  // Update platforms when publishedAccounts changes
  useEffect(() => {
    setPlatformsMap(getPlatformsMap());
  }, [publishedAccounts]);

  const handleSocialAccountSelect = async (value) => {
    try {
      updateSocialAccountLoadingState(LoadingState.Loading);

      await socialAccountStore.updateSocialAccountInfo(value);
      await socialAccountStore.fetchAll();
    } catch (e) {
      console.error(`failed getting latest social account info`, e);
    } finally {
      updateSocialAccountLoadingState(LoadingState.Loaded);
    }
  };

  const handleScheduleUpdate = async (datetime) => {
    if (!datetime) {
      try {
        await videoStore.cancelSchedule({ videoId: video?._id || '' });

        notificationApi.success({
          placement: 'top',
          message: t('common.success'),
          description: t('videoEditor.socialPublish.scheduledCanceled'),
        });
      } catch (e) {
        console.error(`failed canceling schedule`, e);
      }
    }
    setPublishAt(datetime);
  };

  const publish = async () => {
    if (!video) {
      return;
    }

    if (!publishedAccounts || publishedAccounts.length === 0) {
      return;
    }

    try {
      updateLoadingState(LoadingState.Loading);

      let publishAtUTC;

      if (publishAt) {
        publishAtUTC = dayjs(publishAt).tz(timezone).utc().toDate();
      }

      const result = await videoStore.updateSocialPublish({
        videoId: video._id,
        publishedAccounts,
        timezone,
        publishAt: publishAtUTC,
      });

      if (!result) {
        throw new Error(`failed publish`);
      }

      notificationApi.success({
        placement: 'top',
        message: publishAt
          ? t('videoEditor.socialPublish.scheduledSuccess')
          : t('videoEditor.socialPublish.publishSuccess'),
        description: publishAt
          ? ''
          : t('videoEditor.socialPublish.publishSuccessDesc'),
      });

      // TODO: just refetch video
      fetchVideoAndAssets();
    } catch (e) {
      notificationApi.error({
        placement: 'top',
        message: t('videoEditor.socialPublish.publishError'),
        description: t('videoEditor.socialPublish.publishErrorDesc'),
      });

      console.error(`failed publishing`, e);
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  const isMoreThanFiveMinutesBefore = video?.socialPublishInfo?.publishAt
    ? dayjs()
        .utc()
        .isBefore(
          dayjs(video?.socialPublishInfo.publishAt).utc().subtract(5, 'minutes')
        )
    : true;

  const getActivePublishedAccount = () => {
    if (activeTab === 'add') return null;
    return platformsMap.get(activeTab) || null;
  };

  const isVideoPublished =
    video?.status === VideoStatus.Published &&
    Boolean(getActivePublishedAccount()?.externalVideoId);

  const handleShare = () => {
    if (!video || !video?.videoUrl) {
      return;
    }
    shareModalRef.current?.open({
      title: video.title || '',
      description: video.description || '',
      url: video.videoUrl,
    });
  };

  const handlePublishedAccountUpdate = async (updatedAccount) => {
    await handleSocialAccountSelect(updatedAccount.socialAccountId);

    // Update or add the account to publishedAccounts
    setPublishedAccounts((prev: any) => [
      ...prev.filter(
        (acc) => acc.socialPlatform !== updatedAccount.socialPlatform
      ),
      updatedAccount,
    ]);
  };

  const handlePublishedAccountRemove = (accountToRemove) => {
    // Remove the account from publishedAccounts
    setPublishedAccounts((prev: any) =>
      prev.filter(
        (acc) => acc.socialPlatform !== accountToRemove.socialPlatform
      )
    );
  };

  return (
    <Card
      title={t('videoEditor.socialPublish.title')}
      extra={
        <Button disabled={!video?.videoUrl} onClick={handleShare}>
          {t('videoEditor.socialPublish.manualShare')}
        </Button>
      }
    >
      <Row gutter={[15, 15]}>
        <Col md={24} lg={12}>
          <span>
            {t('videoEditor.socialPublish.publishDate')}{' '}
            <Typography.Text type={'secondary'}>
              {t('videoEditor.socialPublish.localTime')}
            </Typography.Text>
          </span>
          <Flex style={{ flexDirection: 'column' }} justify={'space-between'}>
            <DatePicker
              style={{ width: '100%' }}
              placeholder={t(
                'videoEditor.socialPublish.publishDatePlaceholder'
              )}
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              value={publishAt ? dayjs(publishAt) : null}
              onChange={handleScheduleUpdate}
              allowClear
            />
          </Flex>
        </Col>
        <Col md={24} lg={12}>
          <Typography.Text>
            {t('videoEditor.socialPublish.timezone')}
          </Typography.Text>
          <Select
            style={{ width: '100%' }}
            placeholder={t('videoEditor.socialPublish.timezone')}
            value={timezone}
            onChange={setTimezone}
          >
            {Object.entries(Timezone).map(([key, value]) => (
              <Select.Option key={value} value={value}>
                {TimezoneLabels[value]}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col md={24} lg={24}>
          <Typography.Text type="secondary">
            <ul style={{ listStyle: 'none', padding: 0, marginBottom: 0 }}>
              <li style={{ marginBottom: 4, marginLeft: 0, fontSize: '13px' }}>
                {t('videoEditor.socialPublish.publishImmediately')}
              </li>
              <li style={{ marginLeft: 0, fontSize: '13px' }}>
                {t('videoEditor.socialPublish.modifyPublishDate')}
              </li>
            </ul>
          </Typography.Text>
        </Col>
        <Divider style={{ margin: '5px 0' }} />

        <Col span={24}>
          <Typography.Text>
            {t('videoEditor.socialPublish.socialAccount')}
          </Typography.Text>
          <SocialAccountTabs
            platformsMap={platformsMap}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            publishedAccounts={publishedAccounts}
            onPublishedAccountUpdate={handlePublishedAccountUpdate}
            onPublishedAccountRemove={handlePublishedAccountRemove}
          />
          {/*<SocialAccountSelect*/}
          {/*  loading={socialAccountLoadingState === LoadingState.Loading}*/}
          {/*  onChange={handleSocialAccountSelect}*/}
          {/*  value={socialAccountId}*/}
          {/*/>*/}
          {/*<Divider style={{ margin: '15px 0' }} />*/}
          {/*{getSocialProviderAdditionalInfo()}*/}
        </Col>

        <Button
          onClick={publish}
          loading={loadingState === LoadingState.Loading}
          disabled={
            publishedAccounts.length === 0 ||
            isVideoPublished ||
            (!video?.videoUrl && !publishAt)
          }
          style={{ width: '100%' }}
          type="primary"
        >
          {publishAt
            ? t('videoEditor.socialPublish.schedule')
            : t('videoEditor.socialPublish.publishButton')}
        </Button>
        <Space size={5} direction={'vertical'} style={{ width: '100%' }}>
          {!video?.videoUrl && !publishAt ? (
            <Typography.Text type="secondary">
              {t('videoEditor.socialPublish.renderRequired')}
            </Typography.Text>
          ) : null}
          {isVideoPublished ? (
            <Typography.Text type="secondary">
              {t('videoEditor.socialPublish.videoAlreadyPublished')}
            </Typography.Text>
          ) : null}
        </Space>
      </Row>
      <ShareModal ref={shareModalRef} />
    </Card>
  );
};

export default observer(SocialPublish);
